import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './globals.css';

// ============================================================
// GLOBAL CLEANUP - Remove all legacy auth flags
// ============================================================
const LEGACY_AUTH_FLAGS = [
  'profile_complete_confirmed',
  'bankid_auth_complete',
  'bankid_auth_timestamp',
  'pwa-bankid-auth-completed-at',
  'zip_sess_jwt',
  'bankid_supabase_credentials',
  'onboarding_complete',
  'bankid_data',
  'bankid_link_mode',
  'bankid_restart_attempted',
  'bankid_trace_id',
  'auth_state',
  'autostart_done',
  'onboarding_firstName',
  'onboarding_lastName',
  'onboarding_personal_number_hash',
  'onboarding_device_code',
  'email_onboarding_complete'
];

LEGACY_AUTH_FLAGS.forEach(key => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // Ignore
  }
});

// Clear pwa_reloading flag on fresh page load (set by device-switch code
// to suppress ErrorBoundary flash during intentional reloads)
try { sessionStorage.removeItem('pwa_reloading'); } catch {}

console.log('[Cleanup] Removed', LEGACY_AUTH_FLAGS.length, 'legacy auth flags');

// ============================================================
// BUILD BANNER - Identifiera vilken app som körs
// ============================================================
const buildTime = new Date().toISOString();
console.log(`
╔═══════════════════════════════════════╗
║  SW CLEARED - FRESH LOAD             ║
║  APP: PWA HANDEL (apps/pwa-handel)   ║
║  BUILD: ${new Date().toISOString()}  ║
║  VERSION: ${import.meta.env.VITE_APP_VERSION || 'dev'} ║
╚═══════════════════════════════════════╝
`);

// ============================================================
// HISTORY SPÅRNING - Debugga smygnavigationer
// ============================================================
if (import.meta.env.DEV) {
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    console.log('[History] pushState:', args[2], '\n', new Error().stack);
    return originalPushState.apply(this, args);
  };
  
  history.replaceState = function(...args) {
    console.log('[History] replaceState:', args[2], '\n', new Error().stack);
    return originalReplaceState.apply(this, args);
  };
}

// Disable browser's automatic scroll restoration
// This ensures pages always load at the top when navigating
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// ============================================================
// LAYER 1: Bootstrap Slug Saving (IIFE for early execution)
// ============================================================
import { RESERVED_PATHS, normalizePath } from './lib/routeConfig';

(function bootstrapSlug() {
  const bootstrapPath = normalizePath(window.location.pathname);
  
  const match = bootstrapPath.match(/^\/([a-z0-9-]+)$/);
  if (match && !RESERVED_PATHS.has(bootstrapPath)) {
    const slug = match[1];
    
    // Save in BOTH sessionStorage AND global variable (double defense)
    // Always include leading slash for consistent absolute navigation
    sessionStorage.setItem('last_slug_path', '/' + slug);
    sessionStorage.setItem('last_slug_url', window.location.href);
    sessionStorage.setItem('was_on_slug', '1');
    
    // Global fallback for absolute safety
    (window as any).__BOOTSTRAP_SLUG__ = slug;
    
    console.log('[bootstrap] last_slug_path =', slug);
    console.log('[bootstrap] Saved to window.__BOOTSTRAP_SLUG__');
  }
})();

// ============================================================
// LAYER 2: React Router Bootstrap
// ============================================================
// All routing handled directly at root level (no /app/ prefix)
const currentPath = normalizePath(window.location.pathname);
console.log('[Router] Mounting PWA Handel on path:', currentPath);

// ============================================================
// CHUNK LOAD ERROR RECOVERY - Stale deployment auto-reload (Layer 2)
// ============================================================
// Safety net for chunk errors that may not reach ErrorBoundary (e.g.
// async rejections on mobile Safari before React's Suspense boundary fires).
// Uses the same sessionStorage key and cooldown as ErrorBoundary.tsx.
const CHUNK_RELOAD_KEY = 'chunk_reload_at';
const CHUNK_RELOAD_COOLDOWN_MS = 15_000;

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason;
  const msg: string = error?.message ?? '';
  const name: string = error?.name ?? '';
  const isChunkError =
    name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed');

  if (!isChunkError) return;

  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const lastReloadAt = raw ? parseInt(raw, 10) : 0;
    if (Date.now() - lastReloadAt > CHUNK_RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
      event.preventDefault(); // Suppress browser's unhandled rejection output
      window.location.reload();
    }
  } catch {
    // sessionStorage unavailable — skip silently
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter basename="">
      <App />
    </BrowserRouter>
  </React.StrictMode>,
);
